import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type DriftEvent, useDriftTimeline } from '@/lib/hooks/useDrift';
import { cn } from '@/lib/utils';

export function DriftPage() {
  const { data, isLoading } = useDriftTimeline();

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.points.map((p) => ({
      label: new Date(p.completed_at).toLocaleDateString(),
      timestamp: new Date(p.completed_at).getTime(),
      score: p.overall_score,
      auditId: p.audit_id,
    }));
  }, [data]);

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Drift</h1>
        <p className="mt-1 text-sm text-slate-500">
          How your AI-agent readiness score is changing over time.
        </p>
      </header>

      {isLoading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}

      {data && data.points.length === 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>No audit history yet</CardTitle>
            <CardDescription>
              Drift only shows up after at least one complete audit.{' '}
              <Link to="/app/audits" className="underline">Run one</Link>.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {data && data.points.length > 0 && (
        <>
          {/* Score chart */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Overall score</CardTitle>
              <CardDescription>{data.points.length} audits in the timeline</CardDescription>
            </CardHeader>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} stroke="#94a3b8" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 6, color: '#f8fafc', fontSize: 12 }}
                    formatter={(value: number) => [`${value}/100`, 'Score']}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#2563eb' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* PR summary */}
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <SummaryCard
              label="PRs reviewed (30d)"
              value={data.pr_summary_30d.prs_reviewed}
            />
            <SummaryCard
              label="With new findings"
              value={data.pr_summary_30d.with_findings}
            />
            <SummaryCard
              label="Skipped (bots / no relevant files)"
              value={data.pr_summary_30d.prs_skipped}
            />
          </div>

          {/* Events list */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Significant changes</CardTitle>
              <CardDescription>Per-dimension score moves of 2 or more between consecutive audits.</CardDescription>
            </CardHeader>
            {data.events.length === 0 ? (
              <p className="text-sm text-slate-500">No material drift detected. Stable.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.events.slice(0, 25).map((e) => <DriftEventRow key={`${e.to_audit_id}-${e.dimension_id}`} event={e} />)}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold">{value}</div>
    </Card>
  );
}

function DriftEventRow({ event }: { event: DriftEvent }) {
  const positive = event.delta > 0;
  return (
    <li className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
      <div>
        <div className="font-medium">{event.dimension_name}</div>
        <div className="text-xs text-slate-500">
          <Link to="/app/audits/$auditId" params={{ auditId: event.to_audit_id }} className="underline-offset-4 hover:underline">
            audit {event.to_audit_id.slice(0, 8)}
          </Link>
          {' · '}
          {new Date(event.occurred_at).toLocaleString()}
        </div>
      </div>
      <div className={cn('text-sm font-mono', positive ? 'text-[var(--color-success)]' : 'text-[var(--color-critical)]')}>
        {event.before} → {event.after} ({positive ? '+' : ''}{event.delta})
      </div>
    </li>
  );
}
